import React from 'react';

import Alert from '@mui/material/Alert';
import { dashboardConfig } from 'config/routeConfigurations/dashboardConfig/dashboardConfig';
import { Link, NavLink } from 'react-router-dom';
import colorsVars from 'styles/colors.module.scss';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import useUserHook from 'hooks/useUserHook';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import MainUserImg from 'components/App/AuthenticateLayout/Header/MainUserImg/mainUserImg';
import Notification from 'components/App/AuthenticateLayout/Header/Notification/Notification';
import UserMenu from 'components/App/AuthenticateLayout/Header/UserMenu/userMenu';
import classes from 'components/App/AuthenticateLayout/Header/header.module.scss';
import { sideMenu as excellenceAppsRoutes } from 'components/Dashboards/ExcellenceApps/routes';
import { sideMenu as planningRoutes } from 'components/Dashboards/Planning/routes';
import { sideMenu as portfolioRoutes } from 'components/Dashboards/Portfolio/routes';
import { sideMenu as projectRoutes } from 'components/Dashboards/Project/ProjectWrapper/projectRoutes';
import { CustomizedTooltip } from 'components/common';

const { headerIconColor } = colorsVars;
const ICON_SIZE = `2.4rem`;

const Header = () => {
    const { ability, username, isSwitchUser } = useUserHook();

    const hasPlanningPermissions = planningRoutes.some((item) => {
        return item.permissions ? ability.can('view', item.permissions) : true;
    });

    const hasExecutionPermissions = projectRoutes.some((item) => {
        return item.permissions ? ability.can('view', item.permissions) : true;
    });

    const hasRiskPermissions = excellenceAppsRoutes.some((item) => {
        return item.permissions ? ability.can('view', item.permissions) : true;
    });

    const hasPortfolioPermissions = portfolioRoutes.some((item) => {
        return item.permissions ? ability.can('view', item.permissions) : true;
    });

    const isReportingAbility = ability.can('view', 'ReportsView');

    return (
        <div className={classes.mainHeaderComponent}>
            {isSwitchUser && (
                <Alert severity={'warning'} className={classes.alertSwitchUser}>
                    Note you are currently logged in as {username}. Remember to Logout to exit session.
                </Alert>
            )}

            <div className={classes.mainHeaderContainer}>
                <div className={classes.leftSection}>
                    <Link to={'/'}>
                        <CustomizedTooltip
                            tooltipPosition={TooltipPosition.Bottom}
                            tooltipContent={`Home`}
                            triggerElement={
                                <div className={classes.imgWrapper}>
                                    <img
                                        className={classes.mainHeaderOrgImg}
                                        src={require('styles/assets/png/foresight_logo.png')}
                                        alt={'Foresight'}
                                    />
                                </div>
                            }
                        />
                    </Link>

                    {hasPlanningPermissions && (
                        <>
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={'Schedule-management tools, reviews, and planning analytics.'}
                                triggerElement={
                                    <NavLink
                                        to={AppRoutes.buildPath(AppRoutes.DASHBOARD_PLANNING_PATH)}
                                        className={classes.navItem}
                                        id={'topHeaderNavItemPlanning'}
                                    >
                                        <div className={classes.headerLeftSideElement}>
                                            <div className={classes.icon}>
                                                <MuiIcon
                                                    icon={'route'}
                                                    fontSize={'2.7rem'}
                                                    color={'inherit'}
                                                    type={'outlined'}
                                                />
                                            </div>
                                            <span className={classes.headerElementText}>
                                                {dashboardConfig.planning.title}
                                            </span>
                                        </div>
                                    </NavLink>
                                }
                            />
                            <div className={classes.divider} />
                        </>
                    )}

                    {hasExecutionPermissions && (
                        <>
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={'Tools for daily project activities, progress tracking, and workflows.'}
                                triggerElement={
                                    <NavLink
                                        to={AppRoutes.buildPath(AppRoutes.DASHBOARD_EXECUTION_PATH)}
                                        className={classes.navItem}
                                        id={'topHeaderNavItemProject'}
                                    >
                                        <div className={classes.headerLeftSideElement}>
                                            <div className={classes.icon}>
                                                <MuiIcon
                                                    icon={'assignment_turned_in'}
                                                    fontSize={'2.7rem'}
                                                    color={'inherit'}
                                                    type={'outlined'}
                                                />
                                            </div>
                                            <span className={classes.headerElementText}>
                                                {dashboardConfig.execution.title}
                                            </span>
                                        </div>
                                    </NavLink>
                                }
                            />
                            <div className={classes.divider} />
                        </>
                    )}

                    {hasRiskPermissions && (
                        <>
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={'Risk registers, analytics, predictions, and project controls.'}
                                triggerElement={
                                    <NavLink
                                        to={AppRoutes.buildPath(AppRoutes.DASHBOARD_RISK_APPS_PATH)}
                                        className={classes.navItem}
                                        id={'topHeaderNavItemDashboardProgressOverview'}
                                    >
                                        <div className={classes.headerLeftSideElement}>
                                            <div className={classes.icon}>
                                                <MuiIcon
                                                    icon={'warning_amber'}
                                                    fontSize={'2.7rem'}
                                                    color={'inherit'}
                                                    type={'outlined'}
                                                />
                                            </div>
                                            <span className={classes.headerElementText}>
                                                {dashboardConfig.risk.title}
                                            </span>
                                        </div>
                                    </NavLink>
                                }
                            />
                            <div className={classes.divider} />
                        </>
                    )}

                    {isReportingAbility && (
                        <>
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={'Tools for building and sharing, project or portfolio reports.'}
                                triggerElement={
                                    <NavLink
                                        to={AppRoutes.buildPath(AppRoutes.DASHBOARD_REPORTING_PATH)}
                                        className={classes.navItem}
                                        id={'topHeaderNavItemReporting'}
                                    >
                                        <div className={classes.headerLeftSideElement}>
                                            <div className={classes.icon}>
                                                <MuiIcon
                                                    icon={'analytics'}
                                                    fontSize={'2.7rem'}
                                                    color={'inherit'}
                                                    type={'outlined'}
                                                />
                                            </div>
                                            <span className={classes.headerElementText}>
                                                {dashboardConfig.reporting.title}
                                            </span>
                                        </div>
                                    </NavLink>
                                }
                            />
                            <div className={classes.divider} />
                        </>
                    )}
                    {hasPortfolioPermissions && (
                        <CustomizedTooltip
                            tooltipPosition={TooltipPosition.Bottom}
                            tooltipContent={'Review portfolio status, risks and trends.'}
                            triggerElement={
                                <NavLink
                                    to={AppRoutes.buildPath(AppRoutes.DASHBOARD_PORTFOLIO_PATH)}
                                    className={classes.navItem}
                                    id={'topHeaderNavItemPortfolio'}
                                >
                                    <div className={classes.headerLeftSideElement}>
                                        <div className={classes.icon}>
                                            <MuiIcon
                                                icon={'cases'}
                                                fontSize={'2.7rem'}
                                                color={'inherit'}
                                                type={'outlined'}
                                            />
                                        </div>
                                        <span className={classes.headerElementText}>
                                            {dashboardConfig.portfolio.title}
                                        </span>
                                    </div>
                                </NavLink>
                            }
                        />
                    )}
                </div>
                <div className={classes.rightSection}>
                    <div>
                        <a
                            rel={'noreferrer'}
                            target={'_blank'}
                            className={classes.navItem}
                            id={'topHeaderNavItemHelpdesk'}
                        >
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={`Helpdesk`}
                                triggerElement={
                                    <div className={classes.headerRightSideElement}>
                                        <Icon name={'header-help-desk'} size={ICON_SIZE} color={headerIconColor} />
                                        <span className={classes.headerElementText}>Need Help?</span>
                                    </div>
                                }
                            />
                        </a>
                    </div>
                    <div>
                        <NavLink
                            to={AppRoutes.buildPath(AppRoutes.SETTINGS)}
                            className={classes.navItem}
                            id={'topHeaderNavItemSettings'}
                        >
                            <CustomizedTooltip
                                tooltipPosition={TooltipPosition.Bottom}
                                tooltipContent={`Your account settings`}
                                triggerElement={
                                    <Icon name={'header-settings'} size={ICON_SIZE} color={headerIconColor} />
                                }
                            />
                        </NavLink>
                    </div>
                    <div>
                        <Notification />
                    </div>
                    <div>
                        <div className={classes.imageWrapper}>
                            <MainUserImg />
                        </div>
                    </div>
                    <UserMenu />
                </div>
            </div>
        </div>
    );
};

export default Header;
